import { Subscription } from "rxjs";
import { Application } from "../application/application";
import { replaceNodeContent } from "../dom/dom";
import { Method, ResponseType, xhrPromise } from "../xhr/xhr";
import { REQUEST_ATTRIB_DOMAIN } from "./cabinets";
import { ContainerPanel } from "./panel";

export interface DialogState {
	top?: number
	bottom?: number
	left?: number
	right?: number
}

export interface DialogDefinition extends DialogState {
	// method that can construct (rather than load) the markup from source
	build?: (panel: ContainerPanel, dialogId: string, application: Application) => Dialog,
	// url where to load (rather than build) the markup from
	url?: string,
	onOpen?: (dialog: Dialog) => void,
	onDestroy?: (dialog: Dialog) => void,
	canClose?: (dialog: Dialog) => boolean
}

export class Dialog {
	private subscriptions: Subscription[] = [];
	private definition: DialogDefinition = undefined;

	public constructor(private panel: ContainerPanel, private dialogId: string, private app: Application) {
	}

	public release(): Dialog {
		//console.trace('releasing dialog %s', this.dialogId);
		if (this.subscriptions && this.subscriptions.length > 0) {
			this.subscriptions.forEach(subscription => subscription.unsubscribe());
			this.subscriptions.length = 0;
		}
		if (this.definition && this.definition.onDestroy) {
			this.definition.onDestroy(this);
		}
		this.getContainerNode().innerHTML = '';
		return this;
	}

	/**
	 * Call this method to add subscriptions to content dom elements.
	 * These elements get removed upon each call to Open) -> buildContainer()
	 * so by registering the subscriptions, they get properly unsubscribed.
	 */
	public addSubscription(subscription: Subscription): Dialog {
		this.subscriptions.push(subscription);
		return this;
	}

	public getApplication(): Application {
		return this.app;
	}

	public getPanel(): ContainerPanel {
		return this.panel;
	}

	/**
	 *Gets called by the ContainerPanel immediately after construction of the Dialog object 
	 */
	public setup(definition: DialogDefinition): Dialog {
		this.definition = definition;
		return this;
	}

	public getContainerNodeSelector(): string {
		return `${this.getPanel().getDialogNodeSelector(this.dialogId)}`;
	}

	public getContainerNode(): HTMLDivElement {
		return (<HTMLDivElement>document.querySelector(this.getContainerNodeSelector()));
	}

	public open(): Promise<Dialog> {
		return new Promise<Dialog>((resolve, reject) => {

			if (this.definition) {
				this.buildDialog()
					.then(container => {
						//let external scripts run their thing once the skeleton markup became available
						if (container.definition && container.definition.onOpen) {
							container.definition.onOpen(container);
						}
						resolve(container);
					}).catch(container => {
						//??
						reject(container);
					});
			} else {
				reject(this);
			}
		})
	}
	public close(): boolean {
		if (this.canClose()) {
			this.release();
			return true;
		} else {
			return false;
		}
	}

	private canClose(): boolean {
		if (this.definition && this.definition.canClose) {
			return this.definition.canClose(this);
		}
		return true;
	}

	public destroy(): Dialog {
		this.release();
		let elem = this.getContainerNode();
		elem.parentNode.removeChild(elem);
		return this;
	}

	protected buildDialog(): Promise<Dialog> {
		//to be sure if this happens to be an accidental reload
		this.release();

		return new Promise<Dialog>((resolve, reject) => {
			xhrPromise(
				{
					url: this.definition.url.replace(REQUEST_ATTRIB_DOMAIN, this.getApplication().getDomain()),
					method: Method.get,
					accept: 'text/html',
					responseType: ResponseType.document,
					digester: this.getApplication().getDigester()
				}
			).then((response) => {

				let dialogDiv = <HTMLDivElement>response.data;

				replaceNodeContent(this.getContainerNode(), dialogDiv, false);

				resolve(this);

			}).catch((error) => {
				console.error(error);
				reject(this);
			});
		}
		).then(container => {
			return container.finishDialogBuild();
		});
	}
	/**
	 * Override when sub classes need extra work before the onLoad can be called 
	 */
	protected finishDialogBuild(): Promise<Dialog> {
		return new Promise<Dialog>((resolve, _reject) => resolve(this));
	}
	/**
	 * Override this method to do special processing when we just came into view. 
	 */
	public onFocus(): void {

	}
	/**
	 * Override this method to do special processing when we just go out of view. 
	 */
	public onBlur(): void {

	}

}